"use client";

import { AiOutlineMenu } from "react-icons/ai";
import { FaUserCircle } from "react-icons/fa";
import { BiUserCircle } from "react-icons/bi";
import { signOut, useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import MenuItem from "./MenuItem";
import { SimplifiedUser } from "@/app/types";
import { useCallback } from "react";

interface UserMenuProps {
  currentUser?: SimplifiedUser | null;
  isOpen: boolean;
  toggleOpen: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({
  currentUser,
  isOpen,
  toggleOpen,
}) => {
  const router = useRouter();
  const { data: session, status } = useSession();
  
  const user = status === "authenticated" ? session?.user : currentUser;
  
  const handleSignOut = useCallback(() => {
    const callbackUrl = process.env.NEXTAUTH_URL || "/";
    signOut({ callbackUrl });
  }, []);

  const handleMenuItemClick = useCallback((action: () => void) => {
    action();
    toggleOpen();
  }, [toggleOpen]);

  const menuItems = user
    ? [
        {
          label: "Dashboard",
          path: `/dashboard/${user.id}`,
        },
        {
          label: "My articles",
          path: `/my-articles/${user.id}`,
        },
        {
          label: "Edit profile",
          path: `/editprofile/${user.id}`,
        },
        ...(user.role === "ADMIN"
          ? [{ label: "Count Dashboard", path: "/adm/count" }]
          : []),
        { label: "Logout", onClick: handleSignOut },
      ]
    : [
        { label: "Login", path: "/login" },
        { label: "Sign up", path: "/register" },
      ];

  return (
    <div className="relative">
      <div className="flex flex-row items-center gap-3">
        <div
          onClick={toggleOpen}
          className="
            p-2 md:py-2 md:px-3 border-[1px] 
            border-neutral-600 text-white flex flex-row items-center 
            gap-3 rounded-lg cursor-pointer 
            hover:shadow-md transition
          "
        >
          {!user ? <BiUserCircle size={20} /> : <FaUserCircle size={20} />}
          <div className="hidden md:block text-sm">
            {user?.name || ""}
          </div>
        </div>
      </div>
      {isOpen && (
        <div
          className="
            absolute z-50 rounded-xl shadow-md w-[90vw] sm:w-[70vw] md:w-[50vw] lg:w-[30vw] 
            bg-gray-900 text-white overflow-hidden right-0 top-12 text-sm
          "
        >
          <div className="flex flex-col cursor-pointer">
            {menuItems.map((item, index) => (
              <div key={index}>
                {item.label === "Logout" && (
                  <hr className="border-t border-black" />
                )}
                <MenuItem
                  label={item.label}
                  onClick={() =>
                    handleMenuItemClick(
                      item.onClick
                        ? item.onClick
                        : () => router.push(item.path!),
                    )
                  }
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;